import {Fragment, ReactElement, useState} from 'react'
import {withNamespaces, WithNamespaces, Trans} from 'react-i18next'
import {
  Table, TableBody, TableCell, TableHead, TableRow, Typography, Grid,
  Collapse, IconButton,
} from '@material-ui/core'
import {KeyboardArrowDown, KeyboardArrowUp} from '@material-ui/icons'
import messages from '../../../../../../../assets/messages'
import {formatDate} from '../../../../../../../common/utils'
import Amount from '../../../../../../Common/Amount'
import {TablePropsBase} from '../../types'
import {SubscriptionStatistic} from '../../hooks/useSubscriptionStatistics'
import {PaidPerformanceAmount} from '../PaidPerformanceAmount'
import {useStyles} from './styles'

const SettingsDetail = ({settings}: {settings: NonNullable<SubscriptionStatistic['settings']>}) => {
  const classes = useStyles()

  return (
    <div className={classes.settingsContent}>
      <div className={classes.settingsSection}>
        <Typography variant="subtitle2" className={classes.settingsSectionTitle}>
          <Trans {...messages.strategySettings} />
        </Typography>
        <div className={classes.settingsGrid}>
          <Typography variant="body2" className={classes.settingsLabel}><Trans {...messages.strategyType} /></Typography>
          <Typography variant="body2">{settings.strategy.type}</Typography>
          <Typography variant="body2" className={classes.settingsLabel}><Trans {...messages.strategyDirection} /></Typography>
          <Typography variant="body2">{settings.strategy.direction}</Typography>
          <Typography variant="body2" className={classes.settingsLabel}><Trans {...messages.strategyFundsType} /></Typography>
          <Typography variant="body2">{settings.strategy.fundsType}</Typography>
          <Typography variant="body2" className={classes.settingsLabel}><Trans {...messages.strategyRiskRatio} /></Typography>
          <Typography variant="body2">{settings.strategy.riskRatio}</Typography>
          <Typography variant="body2" className={classes.settingsLabel}><Trans {...messages.strategyLotsStep} /></Typography>
          <Typography variant="body2">{settings.strategy.lotsStep}</Typography>
          <Typography variant="body2" className={classes.settingsLabel}><Trans {...messages.strategyMinVolumeHandling} /></Typography>
          <Typography variant="body2">{settings.strategy.minVolumeHandling}</Typography>
        </div>
      </div>

      <div className={classes.settingsSection}>
        <Typography variant="subtitle2" className={classes.settingsSectionTitle}>
          <Trans {...messages.correctionSettings} />
        </Typography>
        <div className={classes.settingsGrid}>
          <Typography variant="body2" className={classes.settingsLabel}><Trans {...messages.correctionRiskFactor} /></Typography>
          <Typography variant="body2">{settings.correction.riskFactor ?? '-'}</Typography>
          <Typography variant="body2" className={classes.settingsLabel}><Trans {...messages.correctionAction} /></Typography>
          <Typography variant="body2">{settings.correction.action}</Typography>
        </div>
      </div>

      {settings.riskRules.length > 0 && (
        <div className={classes.settingsSection}>
          <Typography variant="subtitle2" className={classes.settingsSectionTitle}>
            <Trans {...messages.riskRules} />
          </Typography>
          <Table size="small" className={classes.riskRulesTable}>
            <TableHead>
              <TableRow>
                <TableCell><Trans {...messages.riskRuleParameter} /></TableCell>
                <TableCell><Trans {...messages.riskRuleThreshold} /></TableCell>
                <TableCell><Trans {...messages.riskRuleSubscriptionAction} /></TableCell>
                <TableCell><Trans {...messages.riskRuleTradingAction} /></TableCell>
                <TableCell><Trans {...messages.riskRuleLevelReached} /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settings.riskRules.map((rule, idx) => (
                <TableRow key={idx}>
                  <TableCell>{rule.parameter}</TableCell>
                  <TableCell>{rule.threshold}</TableCell>
                  <TableCell>{rule.subscriptionAction}</TableCell>
                  <TableCell>{rule.tradingAction}</TableCell>
                  <TableCell>{rule.isLevelReached ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

const Component = (props: TablePropsBase & WithNamespaces): ReactElement => {
  const {lng, subscriptions, isDefaultDate, children} = props
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const classes = useStyles()

  const toggleExpanded = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const hasAnySettings = subscriptions.some(s => s.settings != null)

  return (
    <>
      <Table className={classes.container}>
        <TableHead>
          <TableRow>
            {hasAnySettings && <TableCell style={{width: 48}} />}
            <TableCell className={classes.firstColumn}>
              <Trans {...messages.clientName} />
            </TableCell>
            <TableCell><Trans {...messages.startCopyingDate} /></TableCell>
            <TableCell><Trans {...messages.profitPerPeriod} /></TableCell>
            <TableCell><Trans {...messages.stopCopyingDate} /></TableCell>
            <TableCell><Trans {...messages.performanceFeePaid} /></TableCell>
            {isDefaultDate ?
              <TableCell><Trans {...messages.performanceFee} /></TableCell>
            : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {subscriptions.map(subscription => {
            const {
              id,
              followerFullName,
              startedAt,
              stoppedAt,
              profit,
              pendingPerformanceFee,
              currency,
              settings,
            } = subscription
            const isExpanded = expandedIds.has(id)
            const colSpan = (hasAnySettings ? 1 : 0) + 5 + (isDefaultDate ? 1 : 0)

            return (
              <Fragment key={id}>
                <TableRow
                  className={settings != null ? classes.expandableRow : undefined}
                  onClick={settings != null ? () => toggleExpanded(id) : undefined}
                >
                  {hasAnySettings && (
                    <TableCell style={{width: 48, padding: '0 4px'}}>
                      {settings != null && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleExpanded(id) }}>
                          {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                  <TableCell className={classes.firstColumn}>
                    <Typography variant="body1">
                      {followerFullName ?? '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      {startedAt != null ? formatDate(lng, startedAt) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      <Amount value={profit} currency={currency} locale='en-US' />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      {stoppedAt != null ? formatDate(lng, stoppedAt) : <Trans {...messages.copying} />}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      <PaidPerformanceAmount subscription={subscription} isDefaultDate={isDefaultDate} />
                    </Typography>
                  </TableCell>
                  {isDefaultDate ? (
                    <TableCell>
                      <Typography variant="body1">
                        <Amount
                          value={pendingPerformanceFee}
                          currency={currency}
                          locale='en-US'
                          classes={{root: classes.yellowText}}
                        />
                      </Typography>
                    </TableCell>
                  ) : null}
                </TableRow>
                {settings != null && (
                  <TableRow>
                    <TableCell className={classes.settingsCell} colSpan={colSpan}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <SettingsDetail settings={settings} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
      <Grid item container xs={12}>
        {children}
      </Grid>
    </>
  )
}

export const TableDesktop = withNamespaces()(Component)
